import { Fragment, FunctionComponent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { observer } from "mobx-react-lite";

import { AccountInfo, InteractionRequiredAuthError, InteractionType, SilentRequest } from "@azure/msal-browser";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal, useMsalAuthentication } from "@azure/msal-react";

import { locale, MessageSeverity } from "primereact/api";

import { applicationConfig } from "../../../environmentConfiguration";
import { initLocalization } from "../../../utils/localization/i18n";
import MsGraph, { IAccountWrapper } from "../../../utils/MsGraphApiCall";
import { CustomNavigationClient } from "../../../utils/NavigationClient";
import { AppLanguageOptions } from "../../models/constants/app-language-options.const";
import { addPrimeReactLocales } from "../../models/functions/add-prime-react-locales.function";
import { useStore } from "../../stores/StoreContext";
import { LoadingPage } from "../Pages/FullScreenInformation";
import { UnauthorizedPage } from "../Pages/Unauthorized";
import { App } from "./App";

const RawAuthenticateApp: FunctionComponent = () => {
  const { userStore, toastStore } = useStore();

  const [isAuthenticating, setIsAuthenticating] = useState<boolean>(true);
  const [isLanguageInitializing, setIsLanguageInitializing] = useState<boolean>(true);
  const [authenticationFailed, setAuthenticationFailed] = useState<boolean>(false);
  const [errors, setErrors] = useState<string | null>(null);
  const [account, setAccount] = useState<AccountInfo>();

  const isRequestingAccountInfoRef = useRef<boolean>(false);
  const checkingForActiveAccountRef = useRef<boolean>(false);
  const initializingLocalizationRef = useRef<boolean>(false);

  const navigate = useNavigate();
  const { instance: pca } = useMsal();

  const logError = (error: Error) => {
    console.error(error);
    setErrors(error.message);
  };

  const refreshAccessToken = () => {
    const request: SilentRequest = {
      scopes: applicationConfig.permissionScopes,
      account,
    };

    pca
      .acquireTokenSilent(request)
      .then(response => {
        console.debug(response, "Access token acquired silently");
        userStore.setAccessToken(response.accessToken);
      })
      .catch(error => {
        if (error instanceof InteractionRequiredAuthError) {
          logError(new Error("Failed to acquire access token silently. Redirect to acquire token interactively."));
          pca.acquireTokenRedirect(request);
        } else {
          logError(new Error("Failed to acquire access token silently"));
        }
      });
  };

  useMsalAuthentication(InteractionType.Redirect);

  useEffect(() => {
    if (checkingForActiveAccountRef.current) {
      return;
    }

    let account;
    let remainingRetries = 5;
    const interval = setInterval(() => {
      account = pca.getActiveAccount();

      if (account) {
        setAccount(account);
        clearInterval(interval);
      } else if (remainingRetries <= 0) {
        setAuthenticationFailed(true);
        clearInterval(interval);
      }

      remainingRetries--;
    }, 1000);

    checkingForActiveAccountRef.current = true;
  }, []);

  useEffect(() => {
    // Refresh every 14 minutes (if token expires in 15 min)
    const refreshTime = 14 * 60 * 1000; // 14 minutes in milliseconds

    const interval = setInterval(() => {
      refreshAccessToken();
    }, refreshTime);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isRequestingAccountInfoRef.current || !account) {
      return;
    }

    pca.setNavigationClient(new CustomNavigationClient(navigate));
    MsGraph.requestProfileData(pca, account, InteractionType.Redirect).then(
      ([error, data]: [Error | null, IAccountWrapper | null]) => {
        if (error) {
          logError(error);
        } else if (data?.accessToken) {
          userStore.setUserInfo(data);
          userStore.setAccessToken(data.accessToken);
        } else {
          logError(new Error("No access token found"));
        }
        setIsAuthenticating(false);
      },
    );

    isRequestingAccountInfoRef.current = true;
  }, [account]);

  useEffect(() => {
    const setupLocalization = async () => {
      const i18n = await initLocalization();

      userStore.setLanguageLibrary(i18n);

      if (!!i18n.language && i18n.language !== userStore.currentLanguage.code) {
        const languageOption = userStore.findLanguageOptionByCode(i18n.language);
        userStore.changeLanguage(languageOption ?? userStore.currentLanguage);

        addPrimeReactLocales();
        locale(i18n.language);
      }

      setIsLanguageInitializing(false);
    };

    if (!initializingLocalizationRef.current) {
      initializingLocalizationRef.current = true;
      setupLocalization();
    }
  }, [userStore]);

  useEffect(() => {
    const i18n = userStore.i18nInstance;
    if (i18n?.isInitialized && i18n.resolvedLanguage !== i18n.language) {
      toastStore.addToast({
        severity: MessageSeverity.ERROR,
        summary: { message: "Translation Error" },
        detail: {
          message: `No translations found for language '${AppLanguageOptions.find(option => option.code === i18n.language)?.name}'`,
        },
      });
    }
  }, [userStore.i18nInstance?.isInitialized, userStore.currentLanguage]);

  if (isLanguageInitializing) {
    return <LoadingPage loadingInformation="Initializing language ..." />;
  } else if (authenticationFailed) {
    return <UnauthorizedPage redirectToLogin={() => pca.loginRedirect()} errors={errors} />;
  } else if (isAuthenticating || !userStore.userAccessToken) {
    return <LoadingPage loadingInformation="Authenticating ..." />;
  }

  return (
    <Fragment>
      <AuthenticatedTemplate>
        <App />
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <UnauthorizedPage redirectToLogin={() => pca.loginRedirect()} errors={errors} />
      </UnauthenticatedTemplate>
    </Fragment>
  );
};

export const AuthenticateApp = observer(RawAuthenticateApp);
